import React, {
  useState,
  useRef,
  useMemo,
  useEffect,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";
import { useReactToPrint } from "react-to-print";
import { useUIContext } from "../../../../context/UIContext";
import { useAuthContext } from "../../../../context/AuthContext";
import useLessonPlans, {
  useLessonPlanSamples,
} from "../../../../hooks/useLessonPlans";
import styles from "./SettingLessonPlanModal.module.css";

export default function SettingLessonPlanModal({
  lessonInfo,
  onClose,
  settingData = [],
  termPeriodData = [],
  lessonsData = [],
  schoolId,
}) {
  const { t } = useTranslation();
  const { handleToastCenterTop, setToastConfirmModalState } = useUIContext();
  const { hasPermission } = useAuthContext();

  // 번역 키 prefix
  const tp = "teacher.lessonRelated.lessonPlanModal";

  const printRef = useRef(null);

  // 수업계획서 데이터 상태
  const [lessonPlanData, setLessonPlanData] = useState({
    classDay: "",
    objectives: "",
    preparation: "",
    evaluationMethod: "",
    weeklyPlans: [{ id: -Date.now(), week: "1주차", content: "", note: "" }],
  });

  // 서버에서 가져온 수업계획서 데이터 상태
  const [loadedLessonPlanData, setLoadedLessonPlanData] = useState(null);

  // 수업계획서 불러오기 관련 상태
  const [loadSchoolYearId, setLoadSchoolYearId] = useState("");
  const [loadTermId, setLoadTermId] = useState("");
  const [loadSelectedLessonId, setLoadSelectedLessonId] = useState("");
  const [showLoadSection, setShowLoadSection] = useState(false);

  // 드래그앤드롭 상태
  const [draggedRow, setDraggedRow] = useState(null);
  const [dragOverRowId, setDragOverRowId] = useState(null);

  // 변경사항 추적
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // API 훅 (상태 정의 후에 호출)
  const { lessonPlanQuery, upsertLessonPlan, fetchLessonPlanById } =
    useLessonPlans({
      schoolid: schoolId,
    });

  const lessonPlanQueryRef = useRef(lessonPlanQuery);

  const { samplesQuery, createSample } = useLessonPlanSamples({
    schoolid: schoolId,
    school_year_id: loadSchoolYearId || undefined,
    term_id: loadTermId || undefined,
  });

  // 기존 수업계획서 로드 (lessonInfo.id가 변경될 때 데이터 가져오기)
  useEffect(() => {
    if (lessonInfo?.id) {
      lessonPlanQueryRef.current.mutate(
        { lessonId: lessonInfo.id, schoolid: schoolId },
        {
          onSuccess: (data) => {
            setLoadedLessonPlanData(data);
          },
          onError: (error) => {
            console.error("수업계획서 조회 실패:", error);
            setLoadedLessonPlanData(null);
          },
        }
      );
    } else {
      setLoadedLessonPlanData(null);
    }
  }, [lessonInfo?.id, schoolId]);

  // 기존 수업계획서 데이터 처리
  useEffect(() => {
    if (!loadedLessonPlanData) return;

    const rawData = loadedLessonPlanData;
    // 응답이 { data: {...} } 형태일 수 있음
    const plan = rawData?.data || rawData;

    if (plan && plan.id) {
      setLessonPlanData({
        classDay: plan.class_day || "",
        objectives: plan.objectives || "",
        preparation: plan.preparation || "",
        evaluationMethod: plan.evaluation_method || "",
        weeklyPlans:
          plan.weekly_plans?.length > 0
            ? plan.weekly_plans.map((p, i) => ({
                id: p.id || -(Date.now() + i),
                week: p.week || "",
                content: p.content || "",
                note: p.note || "",
              }))
            : [{ id: -Date.now(), week: "1주차", content: "", note: "" }],
      });
    }
  }, [loadedLessonPlanData]);

  // 학년도 unique 처리 (hierarchy_level: 0, parent_id: 0 조건으로 필터링)
  const uniqueSchoolYears = useMemo(() => {
    if (!settingData || settingData.length === 0) return [];
    return settingData
      .filter((s) => s.hierarchy_level === 0 && s.parent_id === 0)
      .sort((a, b) => b.school_year - a.school_year);
  }, [settingData]);

  // 초기값 설정
  useEffect(() => {
    if (uniqueSchoolYears.length > 0 && !loadSchoolYearId) {
      setLoadSchoolYearId(uniqueSchoolYears[0].id.toString());
    }
  }, [uniqueSchoolYears, loadSchoolYearId]);

  // 선택한 학년도에 해당하는 학기 필터링 (start_date로 정렬, hierarchy 이름 포함)
  const filteredTerms = useMemo(() => {
    if (!termPeriodData || !loadSchoolYearId) return [];

    // hierarchy 이름을 찾기 위한 헬퍼 함수
    const getHierarchyName = (hierarchyCode) => {
      if (!hierarchyCode || !settingData) return null;
      const found = settingData.find((s) => s.id_code === hierarchyCode);
      return found?.id_name || null;
    };

    return termPeriodData
      .filter((term) => term.school_year_id.toString() === loadSchoolYearId)
      .map((term) => ({
        ...term,
        hierarchyName: getHierarchyName(term.applied_hierarchy_one),
      }))
      .sort((a, b) => String(a.start_date).localeCompare(String(b.start_date)));
  }, [termPeriodData, loadSchoolYearId, settingData]);

  // 학년도 변경 시 첫 번째 학기 자동 선택
  useEffect(() => {
    if (filteredTerms.length > 0) {
      setLoadTermId(filteredTerms[0].id.toString());
    } else {
      setLoadTermId("");
    }
  }, [filteredTerms]);

  // 선택한 학년도/학기에 해당하는 수업 목록 필터링
  const filteredLessons = useMemo(() => {
    if (!lessonsData || !loadSchoolYearId) return [];
    let filtered = lessonsData.filter(
      (lesson) => lesson.school_year_id.toString() === loadSchoolYearId
    );
    if (loadTermId) {
      filtered = filtered.filter(
        (lesson) => lesson.term_id.toString() === loadTermId
      );
    }
    return filtered;
  }, [lessonsData, loadSchoolYearId, loadTermId]);

  // 학년도 표시 텍스트
  const schoolYearText = useMemo(() => {
    if (!lessonInfo) return "";
    const found = settingData?.find((s) => s.id === lessonInfo.school_year_id);
    return found?.school_year || "";
  }, [lessonInfo, settingData]);

  // 학기 표시 텍스트
  const termText = useMemo(() => {
    if (!lessonInfo) return "";
    return lessonInfo.termName + lessonInfo.TermDates.term_category || "";
  }, [lessonInfo]);

  // 담당교사 텍스트
  const teachersText = useMemo(() => {
    if (!lessonInfo?.teachers) return "";
    return lessonInfo.teachers.map((t) => t.tname).join(", ");
  }, [lessonInfo]);

  // 대상 (학년-반)
  const targetText = useMemo(() => {
    if (!lessonInfo) return "";
    const parts = [];
    if (lessonInfo.hierarchyTwo) parts.push(lessonInfo.hierarchyTwo);
    if (lessonInfo.hierarchyThree) parts.push(lessonInfo.hierarchyThree);
    return parts.join("-") || "전체";
  }, [lessonInfo]);

  // 구분 (과정-분류)
  const categoryText = useMemo(() => {
    if (!lessonInfo) return "";
    const parts = [];
    if (lessonInfo.hierarchyOne) parts.push(lessonInfo.hierarchyOne);
    if (lessonInfo.extra_attributes?.subjectSort) {
      parts.push(lessonInfo.extra_attributes.subjectSort);
    }
    return parts.join("-") || "-";
  }, [lessonInfo]);

  // textarea 자동 높이 조절 함수
  const autoResize = useCallback((element) => {
    if (!element) return;
    element.style.height = "auto";
    element.style.height = element.scrollHeight + "px";
  }, []);

  // textarea onChange와 함께 높이 조절
  const handleTextareaChange = useCallback(
    (field, value, e) => {
      setLessonPlanData((prev) => ({
        ...prev,
        [field]: value,
      }));
      setHasUnsavedChanges(true);
      autoResize(e.target);
    },
    [autoResize]
  );

  // 입력 필드 변경 핸들러
  const handleInputChange = (field, value) => {
    setLessonPlanData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setHasUnsavedChanges(true);
  };

  // 주차별 계획 변경 핸들러 (자동 높이 조절 포함)
  const handleWeeklyPlanChange = useCallback(
    (id, field, value, e) => {
      setLessonPlanData((prev) => ({
        ...prev,
        weeklyPlans: prev.weeklyPlans.map((plan) =>
          plan.id === id ? { ...plan, [field]: value } : plan
        ),
      }));
      setHasUnsavedChanges(true);
      if (e?.target) {
        autoResize(e.target);
      }
    },
    [autoResize]
  );

  // 컴포넌트 마운트 후 모든 textarea 높이 조절
  useEffect(() => {
    const textareas = document.querySelectorAll(
      `.${styles.autoResizeTextarea}`
    );
    textareas.forEach((textarea) => autoResize(textarea));
  }, [lessonPlanData, autoResize]);

  // 주차 행 추가
  const addWeeklyPlanRow = () => {
    const newId = -Date.now();
    const lastPlan =
      lessonPlanData.weeklyPlans[lessonPlanData.weeklyPlans.length - 1];

    // 마지막 주차에서 숫자 추출하여 다음 주차 생성
    let nextWeekNum = lessonPlanData.weeklyPlans.length + 1;
    if (lastPlan?.week) {
      const match = lastPlan.week.match(/(\d+)/);
      if (match) {
        nextWeekNum = parseInt(match[1]) + 1;
      }
    }

    setLessonPlanData((prev) => ({
      ...prev,
      weeklyPlans: [
        ...prev.weeklyPlans,
        { id: newId, week: `${nextWeekNum}주차`, content: "", note: "" },
      ],
    }));
    setHasUnsavedChanges(true);
  };

  // 주차 행 삭제
  const deleteWeeklyPlanRow = (id) => {
    if (lessonPlanData.weeklyPlans.length <= 1) {
      handleToastCenterTop(
        t(`${tp}.minOneWeek`) || "최소 1개의 주차 항목이 필요합니다."
      );
      return;
    }

    setLessonPlanData((prev) => ({
      ...prev,
      weeklyPlans: prev.weeklyPlans.filter((plan) => plan.id !== id),
    }));
    setHasUnsavedChanges(true);
  };

  // 드래그 시작
  const handleDragStart = (e, row) => {
    setDraggedRow(row);
    e.dataTransfer.effectAllowed = "move";
  };

  // 드래그 오버
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  // 드래그 진입
  const handleDragEnter = (e, row) => {
    e.preventDefault();
    setDragOverRowId(row.id);
  };

  // 드래그 떠남
  const handleDragLeave = () => {
    setDragOverRowId(null);
  };

  // 드롭
  const handleDrop = (e, targetRow) => {
    e.preventDefault();
    setDragOverRowId(null);

    if (!draggedRow || draggedRow.id === targetRow.id) {
      setDraggedRow(null);
      return;
    }

    const draggedIndex = lessonPlanData.weeklyPlans.findIndex(
      (p) => p.id === draggedRow.id
    );
    const targetIndex = lessonPlanData.weeklyPlans.findIndex(
      (p) => p.id === targetRow.id
    );

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedRow(null);
      return;
    }

    const reorderedPlans = [...lessonPlanData.weeklyPlans];
    const [removed] = reorderedPlans.splice(draggedIndex, 1);
    reorderedPlans.splice(targetIndex, 0, removed);

    setLessonPlanData((prev) => ({
      ...prev,
      weeklyPlans: reorderedPlans,
    }));
    setDraggedRow(null);
    setHasUnsavedChanges(true);

    handleToastCenterTop(
      t(`${tp}.orderChanged`) || "순서가 변경되었습니다.",
      2000,
      "#28a745"
    );
  };

  // 드래그 종료
  const handleDragEnd = () => {
    setDraggedRow(null);
    setDragOverRowId(null);
  };

  // 수업계획서 불러오기 토글
  const toggleLoadSection = () => {
    setShowLoadSection(!showLoadSection);
  };

  // 불러오기 학년도 변경
  const handleLoadSchoolYearChange = (e) => {
    setLoadSchoolYearId(e.target.value);
    setLoadTermId("");
    setLoadSelectedLessonId("");
  };

  // 불러오기 학기 변경
  const handleLoadTermChange = (e) => {
    setLoadTermId(e.target.value);
    setLoadSelectedLessonId("");
  };

  // 불러오기 수업 선택
  const handleLoadLessonSelect = (lessonId) => {
    setLoadSelectedLessonId(lessonId);
  };

  // 샘플 목록
  const sampleList = useMemo(() => {
    const data = samplesQuery.data;
    // 배열인 경우 그대로, 객체인 경우 data 속성 확인, 그 외 빈 배열
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.data)) return data.data;
    return [];
  }, [samplesQuery.data]);

  // 선택한 수업계획서 불러오기
  const loadSelectedLessonPlan = async () => {
    if (!loadSelectedLessonId) {
      handleToastCenterTop(
        t(`${tp}.selectLesson`) || "불러올 수업을 선택해주세요."
      );
      return;
    }

    // [샘플] 선택한 경우 (sample_로 시작하는 ID)
    if (loadSelectedLessonId.startsWith("sample_")) {
      const sampleId = loadSelectedLessonId.replace("sample_", "");
      const selectedSample = sampleList.find(
        (s) => s.id.toString() === sampleId
      );

      if (selectedSample) {
        // 샘플 이름 생성 (학년도-학기-샘플 형태)
        const sampleSchoolYear = settingData?.find(
          (s) => s.id === selectedSample.school_year_id
        )?.school_year;
        const sampleTerm = termPeriodData?.find(
          (t) => t.id === selectedSample.term_id
        )?.term_name;
        const displayName =
          sampleSchoolYear && sampleTerm
            ? `${sampleSchoolYear}-${sampleTerm}-샘플`
            : selectedSample.sample_name || "샘플";

        setLessonPlanData({
          classDay: selectedSample.class_day || "",
          objectives: selectedSample.objectives || "",
          preparation: selectedSample.preparation || "",
          evaluationMethod: selectedSample.evaluation_method || "",
          weeklyPlans:
            selectedSample.weekly_plans?.length > 0
              ? selectedSample.weekly_plans.map((p, i) => ({
                  id: -(Date.now() + i),
                  week: p.week || "",
                  content: p.content || "",
                  note: p.note || "",
                }))
              : [{ id: -Date.now(), week: "1주차", content: "", note: "" }],
        });
        setHasUnsavedChanges(true);
        handleToastCenterTop(
          `"${displayName}"${t(`${tp}.loaded`) || " 을 불러왔습니다."}`,
          2000,
          "#28a745"
        );
        setShowLoadSection(false);
        return;
      }
    }

    // 실제 수업 선택한 경우 (lesson_로 시작하는 ID)
    if (loadSelectedLessonId.startsWith("lesson_")) {
      const lessonId = loadSelectedLessonId.replace("lesson_", "");
      const selectedLesson = filteredLessons.find(
        (l) => l.id.toString() === lessonId
      );

      if (selectedLesson) {
        // 해당 수업의 수업계획서를 훅으로 조회
        try {
          const rawData = await fetchLessonPlanById(selectedLesson.id, {
            schoolid: schoolId,
          });
          // 응답이 { data: {...} } 형태일 수 있음
          const lessonPlan = rawData?.data || rawData;

          if (lessonPlan && lessonPlan.id) {
            setLessonPlanData({
              classDay: lessonPlan.class_day || "",
              objectives: lessonPlan.objectives || "",
              preparation: lessonPlan.preparation || "",
              evaluationMethod: lessonPlan.evaluation_method || "",
              weeklyPlans:
                lessonPlan.weekly_plans?.length > 0
                  ? lessonPlan.weekly_plans.map((p, i) => ({
                      id: -(Date.now() + i),
                      week: p.week || "",
                      content: p.content || "",
                      note: p.note || "",
                    }))
                  : [
                      {
                        id: -Date.now(),
                        week: "1주차",
                        content: "",
                        note: "",
                      },
                    ],
            });
            setHasUnsavedChanges(true);
            handleToastCenterTop(
              `"${selectedLesson.subject_name}"${
                t(`${tp}.planLoaded`) || " 수업계획서를 불러왔습니다."
              }`,
              2000,
              "#28a745"
            );
          } else {
            handleToastCenterTop(
              t(`${tp}.noSavedPlan`) || "해당 수업에 저장된 계획서가 없습니다."
            );
          }
        } catch (error) {
          console.error("수업계획서 조회 실패:", error);
          handleToastCenterTop(
            t(`${tp}.noSavedPlan`) || "해당 수업에 저장된 계획서가 없습니다."
          );
        }
        setShowLoadSection(false);
      }
    }
  };

  // 저장 (upsert 사용 - lesson_id 기준으로 생성 또는 수정)
  const handleSave = async () => {
    if (!lessonInfo?.id) {
      handleToastCenterTop(t(`${tp}.noLessonInfo`) || "수업 정보가 없습니다.");
      return;
    }

    setIsSaving(true);

    const saveData = {
      lesson_id: lessonInfo.id,
      schoolid: schoolId,
      class_day: lessonPlanData.classDay,
      objectives: lessonPlanData.objectives,
      preparation: lessonPlanData.preparation,
      evaluation_method: lessonPlanData.evaluationMethod,
      weekly_plans: lessonPlanData.weeklyPlans.map((p, index) => ({
        week: p.week,
        content: p.content,
        note: p.note,
        order: index,
      })),
    };

    try {
      await upsertLessonPlan.mutateAsync(saveData);
      setHasUnsavedChanges(false);

      // 저장 후 데이터 다시 가져오기
      if (lessonInfo?.id) {
        lessonPlanQueryRef.current.mutate(
          { lessonId: lessonInfo.id, schoolid: schoolId },
          {
            onSuccess: (data) => {
              setLoadedLessonPlanData(data);
            },
            onError: (error) => {
              console.error("수업계획서 재조회 실패:", error);
            },
          }
        );
      }

      handleToastCenterTop(
        t(`${tp}.saved`) || "저장되었습니다.",
        2000,
        "#28a745"
      );
    } catch (error) {
      console.error("저장 실패:", error);
      handleToastCenterTop(
        t(`${tp}.saveFailed`) || "저장에 실패했습니다. 다시 시도해주세요."
      );
    } finally {
      setIsSaving(false);
    }
  };

  // 인쇄
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `${schoolYearText}-${termText}-${
      lessonInfo?.subject_name || "수업계획서"
    }`,
    pageStyle: `
      @media print {
        @page {
          size: A4 portrait;
          margin: 15mm;
        }
        .no-print {
          display: none !important;
        }
        body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
    `,
  });

  // 닫기
  const handleClose = () => {
    if (hasUnsavedChanges) {
      setToastConfirmModalState({
        visible: true,
        message:
          t(`${tp}.unsavedCloseConfirm`) ||
          "저장되지 않은 변경사항이 있습니다. 정말 닫으시겠습니까?",
        onConfirm: () => {
          onClose();
        },
        onCancel: () => {},
      });
    } else {
      onClose();
    }
  };

  // 샘플 저장 (권한 있는 사용자만) - 학년도/학기당 1개만 유지 (upsert)
  // 상단 불러오기 섹션에서 선택한 학년도/학기 기준으로 저장
  const handleSaveSample = async () => {
    if (!hasPermission(null, "can_create")) {
      handleToastCenterTop(
        t(`${tp}.noSamplePermission`) || "샘플 저장 권한이 없습니다."
      );
      return;
    }

    if (!loadSchoolYearId || !loadTermId) {
      handleToastCenterTop(
        t(`${tp}.selectYearTerm`) || "학년도와 학기를 선택해주세요."
      );
      return;
    }

    setIsSaving(true);

    // 상단에서 선택한 학년도/학기 텍스트 가져오기
    const selectedSchoolYear = uniqueSchoolYears.find(
      (s) => s.id.toString() === loadSchoolYearId
    )?.school_year;
    const selectedTerm = filteredTerms.find(
      (t) => t.id.toString() === loadTermId
    )?.term_name;

    const sampleData = {
      schoolid: schoolId,
      school_year_id: parseInt(loadSchoolYearId),
      term_id: parseInt(loadTermId),
      sample_name: `${selectedSchoolYear}-${selectedTerm}-샘플`,
      class_day: lessonPlanData.classDay,
      objectives: lessonPlanData.objectives,
      preparation: lessonPlanData.preparation,
      evaluation_method: lessonPlanData.evaluationMethod,
      weekly_plans: lessonPlanData.weeklyPlans.map((p, index) => ({
        week: p.week,
        content: p.content,
        note: p.note,
        order: index,
      })),
    };

    try {
      await createSample.mutateAsync(sampleData);
      handleToastCenterTop(
        `${selectedSchoolYear}-${selectedTerm}${
          t(`${tp}.sampleSaved`) || " 샘플이 저장되었습니다."
        }`,
        2000,
        "#28a745"
      );
    } catch (error) {
      console.error("샘플 저장 실패:", error);
      handleToastCenterTop(
        t(`${tp}.sampleSaveFailed`) ||
          "샘플 저장에 실패했습니다. 다시 시도해주세요."
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (!lessonInfo) {
    return null;
  }

  return (
    <div className={styles.modalContainer}>
      <div className={styles.modal}>
        {/* 헤더 - 인쇄 시 숨김 */}
        <div className={`${styles.modalHeader} no-print`}>
          <div className={styles.headerLeft}>
            <h3 className={styles.modalTitle}>
              📋 {schoolYearText}-{termText} {t(`${tp}.title`) || "수업계획서"}
            </h3>
            <button
              type="button"
              className={styles.loadButton}
              onClick={toggleLoadSection}
            >
              {showLoadSection
                ? `▲ ${t(`${tp}.closeLoad`) || "불러오기 닫기"}`
                : `📥 ${t(`${tp}.loadLessonPlan`) || "수업계획서 불러오기"}`}
            </button>
          </div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={handleClose}
          >
            ✕
          </button>
        </div>

        {/* 수업계획서 불러오기 섹션 */}
        {showLoadSection && (
          <div className={`${styles.loadSection} no-print`}>
            <div className={styles.loadFilters}>
              <div className={styles.loadFilterItem}>
                <label>{t(`${tp}.schoolYear`) || "학년도"}</label>
                <select
                  value={loadSchoolYearId}
                  onChange={handleLoadSchoolYearChange}
                  className={styles.loadSelect}
                >
                  {uniqueSchoolYears.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.school_year}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.loadFilterItem}>
                <label>{t(`${tp}.term`) || "학기"}</label>
                <select
                  value={loadTermId}
                  onChange={handleLoadTermChange}
                  className={styles.loadSelect}
                >
                  {filteredTerms.map((term) => (
                    <option key={term.id} value={term.id}>
                      {term.term_name}
                      {term.applied_hierarchy_one
                        ? ` (${term.hierarchy_one_name})`
                        : ""}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className={styles.loadLessonList}>
              <div className={styles.loadLessonListHeader}>
                {t(`${tp}.lessonListHeader`) || "수업 목록 (선택하여 불러오기)"}
              </div>
              <div className={styles.loadLessonItems}>
                {/* 샘플 항목들 */}
                {sampleList.map((sample) => {
                  // 학년도-학기-샘플 형태로 표시
                  const sampleSchoolYear = settingData?.find(
                    (s) => s.id === sample.school_year_id
                  )?.school_year;
                  const sampleTerm = termPeriodData?.find(
                    (t) => t.id === sample.term_id
                  )?.term_name;
                  const displayName =
                    sampleSchoolYear && sampleTerm
                      ? `${sampleSchoolYear}-${sampleTerm}-샘플`
                      : sample.sample_name || "샘플";

                  return (
                    <div
                      key={`sample_${sample.id}`}
                      className={`${styles.loadLessonItem} ${
                        loadSelectedLessonId === `sample_${sample.id}`
                          ? styles.selected
                          : ""
                      }`}
                      onClick={() =>
                        handleLoadLessonSelect(`sample_${sample.id}`)
                      }
                    >
                      <span className={styles.sampleBadge}>
                        [{t(`${tp}.sample`) || "샘플"}]
                      </span>
                      <span>{displayName}</span>
                    </div>
                  );
                })}

                {/* 구분선 */}
                {sampleList.length > 0 && (
                  <div className={styles.loadDivider}>
                    <span>{t(`${tp}.lessonList`) || "수업 목록"}</span>
                  </div>
                )}

                {/* 실제 수업 목록 */}
                {filteredLessons.map((lesson) => (
                  <div
                    key={`lesson_${lesson.id}`}
                    className={`${styles.loadLessonItem} ${
                      loadSelectedLessonId === `lesson_${lesson.id}`
                        ? styles.selected
                        : ""
                    }`}
                    onClick={() =>
                      handleLoadLessonSelect(`lesson_${lesson.id}`)
                    }
                  >
                    <span className={styles.lessonSubject}>
                      {lesson.subject_name}
                    </span>
                    <span className={styles.lessonInfo}>
                      {lesson.hierarchyTwo}-{lesson.hierarchyThree} |{" "}
                      {lesson.teachers?.map((t) => t.tname).join(", ")}
                    </span>
                  </div>
                ))}

                {sampleList.length === 0 && filteredLessons.length === 0 && (
                  <div className={styles.noLessonsMessage}>
                    {t(`${tp}.noLessons`) || "해당 조건의 수업이 없습니다."}
                  </div>
                )}
              </div>

              <div className={styles.loadActions}>
                {hasPermission(null, "can_create") ? (
                  <button
                    className={styles.saveSampleButton}
                    onClick={handleSaveSample}
                    disabled={isSaving}
                  >
                    {isSaving
                      ? t(`${tp}.saving`) || "저장 중..."
                      : t(`${tp}.saveSample`) ||
                        "아래 수업 계획서를 샘플로 저장"}
                  </button>
                ) : (
                  <div></div>
                )}
                <button
                  className={styles.loadConfirmButton}
                  onClick={loadSelectedLessonPlan}
                  disabled={!loadSelectedLessonId}
                >
                  {t(`${tp}.load`) || "불러오기"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 인쇄 영역 */}
        <div className={styles.modalContent} ref={printRef}>
          {/* 인쇄용 제목 */}
          <div className={styles.printHeader}>
            <h2 className={styles.printTitle}>
              {schoolYearText}-{termText} {t(`${tp}.title`) || "수업계획서"}
            </h2>
          </div>

          {/* 수업 기본 정보 테이블 (수업목표, 준비물, 평가방법 포함) */}
          <table className={styles.infoTable}>
            <tbody>
              <tr>
                <th>{t(`${tp}.subjectName`) || "교과명"}</th>
                <td>{lessonInfo.subject_name || "-"}</td>
                <th>{t(`${tp}.target`) || "대상"}</th>
                <td>{targetText}</td>
                <th>{t(`${tp}.teacher`) || "담당 교사"}</th>
                <td>{teachersText || "-"}</td>
              </tr>
              <tr>
                <th>{t(`${tp}.category`) || "구분"}</th>
                <td>{categoryText}</td>
                <th>{t(`${tp}.hours`) || "시수"}</th>
                <td>{lessonInfo.lesson_hours || "-"}</td>
                <th>{t(`${tp}.classDay`) || "수업 요일"}</th>
                <td>
                  <input
                    type="text"
                    className={styles.inlineInput}
                    value={lessonPlanData.classDay}
                    onChange={(e) =>
                      handleInputChange("classDay", e.target.value)
                    }
                  />
                </td>
              </tr>
              <tr>
                <th>{t(`${tp}.objectives`) || "수업목표"}</th>
                <td colSpan={5}>
                  <textarea
                    className={`${styles.autoResizeTextarea} ${styles.inlineTextarea}`}
                    value={lessonPlanData.objectives}
                    onChange={(e) =>
                      handleTextareaChange("objectives", e.target.value, e)
                    }
                    rows={1}
                  />
                </td>
              </tr>
              <tr>
                <th>{t(`${tp}.preparation`) || "준비물"}</th>
                <td colSpan={5}>
                  <textarea
                    className={`${styles.autoResizeTextarea} ${styles.inlineTextarea}`}
                    value={lessonPlanData.preparation}
                    onChange={(e) =>
                      handleTextareaChange("preparation", e.target.value, e)
                    }
                    rows={1}
                  />
                </td>
              </tr>
              <tr>
                <th>{t(`${tp}.evaluationMethod`) || "평가방법"}</th>
                <td colSpan={5}>
                  <textarea
                    className={`${styles.autoResizeTextarea} ${styles.inlineTextarea}`}
                    value={lessonPlanData.evaluationMethod}
                    onChange={(e) =>
                      handleTextareaChange(
                        "evaluationMethod",
                        e.target.value,
                        e
                      )
                    }
                    rows={1}
                  />
                </td>
              </tr>
            </tbody>
          </table>

          {/* 주차별 계획 테이블 */}
          <table className={styles.weeklyTable}>
            <thead>
              <tr>
                <th style={{ width: "120px" }}>{t(`${tp}.week`) || "주"}</th>
                <th>{t(`${tp}.content`) || "내용"}</th>
                <th className={styles.noteHeader}>
                  {t(`${tp}.note`) || "비고"}
                </th>
                <th style={{ width: "80px" }} className="no-print">
                  <button
                    type="button"
                    className={styles.addRowButtonSmall}
                    onClick={addWeeklyPlanRow}
                  >
                    {t(`${tp}.add`) || "추가"}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {lessonPlanData.weeklyPlans.map((plan) => (
                <tr
                  key={plan.id}
                  className={`${styles.weeklyRow} ${
                    dragOverRowId === plan.id ? styles.dragOver : ""
                  } ${draggedRow?.id === plan.id ? styles.dragging : ""}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, plan)}
                  onDragOver={handleDragOver}
                  onDragEnter={(e) => handleDragEnter(e, plan)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, plan)}
                  onDragEnd={handleDragEnd}
                >
                  <td className={styles.weekCell}>
                    <div className={`${styles.dragHandle} no-print`}>⋮⋮</div>
                    <textarea
                      className={`${styles.autoResizeTextarea} ${styles.weekInput}`}
                      value={plan.week}
                      onChange={(e) =>
                        handleWeeklyPlanChange(
                          plan.id,
                          "week",
                          e.target.value,
                          e
                        )
                      }
                      rows={1}
                    />
                  </td>
                  <td>
                    <textarea
                      className={`${styles.autoResizeTextarea} ${styles.contentInput}`}
                      value={plan.content}
                      onChange={(e) =>
                        handleWeeklyPlanChange(
                          plan.id,
                          "content",
                          e.target.value,
                          e
                        )
                      }
                      rows={1}
                    />
                  </td>
                  <td className={styles.noteCell}>
                    <textarea
                      className={`${styles.autoResizeTextarea} ${styles.noteInput}`}
                      value={plan.note}
                      onChange={(e) =>
                        handleWeeklyPlanChange(
                          plan.id,
                          "note",
                          e.target.value,
                          e
                        )
                      }
                      rows={1}
                    />
                  </td>
                  <td className="no-print text-center">
                    <button
                      type="button"
                      className={styles.deleteRowButton}
                      onClick={() => deleteWeeklyPlanRow(plan.id)}
                    >
                      {t(`${tp}.delete`) || "삭제"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 푸터 - 인쇄 시 숨김 */}
        <div className={`${styles.modalFooter} no-print`}>
          {hasUnsavedChanges ? (
            <div className={styles.unsavedChanges}>
              ⚠️{" "}
              {t(`${tp}.unsavedChanges`) ||
                "저장되지 않은 변경사항이 있습니다."}
            </div>
          ) : (
            <div></div>
          )}
          <div className={styles.footerButtons}>
            <button
              type="button"
              className={`${styles.footerButton} ${styles.primary}`}
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving
                ? t(`${tp}.saving`) || "저장 중..."
                : `💾 ${t(`${tp}.save`) || "저장하기"}`}
            </button>
            <button
              type="button"
              className={`${styles.footerButton} ${styles.outline}`}
              onClick={handlePrint}
            >
              🖨️ {t(`${tp}.print`) || "인쇄"}
            </button>
            <button
              type="button"
              className={`${styles.footerButton} ${styles.secondary}`}
              onClick={handleClose}
            >
              {t(`${tp}.close`) || "닫기"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
